import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface ConfirmRemoveWaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  userFullName: string;
  onConfirm: (removalReason?: string) => Promise<void>;
}

function ConfirmRemoveWaitlistModal({
  isOpen,
  onClose,
  userFullName,
  onConfirm,
}: ConfirmRemoveWaitlistModalProps) {
  const [isWorking, setIsWorking] = useState(false);
  const [removalReason, setRemovalReason] = useState("");

  const handleClose = () => {
    setRemovalReason("");
    onClose();
  };

  const handleConfirm = async () => {
    setIsWorking(true);
    try {
      await onConfirm(removalReason.trim() || undefined);
      setRemovalReason("");
      onClose();
    } catch (error) {
      console.error("Failed to remove from waitlist:", error);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog onOpenChange={(open) => !open && handleClose()} open={isOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Remove from Waitlist</DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-400">
            Are you sure you want to remove <strong>{userFullName}</strong> from
            the waitlist?
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <Label htmlFor="removalReason">Removal Reason (Optional)</Label>
          <Textarea
            className="mt-2"
            disabled={isWorking}
            id="removalReason"
            onChange={(e) => setRemovalReason(e.target.value)}
            placeholder="Enter reason for removal..."
            rows={3}
            value={removalReason}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            This will be saved for audit purposes.
          </p>
        </div>

        <DialogFooter>
          <Button disabled={isWorking} onClick={handleClose} variant="outline">
            Cancel
          </Button>
          <Button disabled={isWorking} onClick={handleConfirm}>
            {isWorking ? "Removing..." : "Remove"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfirmRemoveWaitlistModal;
